/* @flow */
import * as React from 'react';
import PropTypes from 'prop-types';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';

import { rootGroupCode } from 'entities/RootGroup';
import { withRouter } from 'app/context/Router';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import NotFoundPage from 'app/components/NotFoundPage';
import Group from 'entities/Group';
import GroupStore from 'groups/stores/GroupStore';

import LOAD_BY_GROUP_CODE_QUERY from '../queries/LoadByGroupCodeQuery.gql';

import type { LoadGroupByCode } from '../queries/__generated__/LoadGroupByCode';
import type { Location } from 'lib/ReactRouterTypes';

type Props = {
  params: Object,
  router: Object,
  location: Location,
  children: React.Node,
  data: ReactApolloData<LoadGroupByCode>,
};

type State = {
  group: ?Group,
  errorCode: ?string,
};

// this components is responsible for loading and passing a
// a backbone group instance into all its children props.
class GroupContainer extends React.Component<Props, State> {
  state = {
    group: null,
    errorCode: null,
  };

  static childContextTypes = {
    group: PropTypes.instanceOf(Group),
  };

  static getDerivedStateFromProps(nextProps: Props) {
    if (nextProps.params.groupCode === rootGroupCode) {
      const group = GroupStore.getGroupForCode(rootGroupCode);
      return { group };
    }

    const { data } = nextProps;
    if (data && data.loading) {
      return null;
    }

    const errorCode = idx(
      data.error,
      _ => _.graphQLErrors[0].serviceMetadata.service_response.code
    );
    if (errorCode) {
      return { errorCode };
    }

    const group = idx(data, _ => _.classes[0]);
    if (group) {
      return {
        group: ((Group.fromJSON(group): any): Group),
      };
    }

    return null;
  }

  getChildContext() {
    return {
      group: this.state.group,
    };
  }

  render() {
    const { data, params, children, router } = this.props;
    const { group, errorCode } = this.state;

    if (data && data.loading) {
      return <CenteredLoadingIndicator />;
    }

    if (errorCode === 'class_not_found') {
      router.replace({ pathname: '/classes/join', query: { code: params.groupCode } });
      return <NotFoundPage />;
    }

    if (!group) {
      console.error('GroupContainer - failed to load group');
      console.warn(errorCode);
      return <NotFoundPage />;
    }

    const groupCode = group.getCodeOrError();

    // When your request gets denied
    if (
      group.getType() === 'available' &&
      router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to default page
      router.replace({ pathname: '/classes', query: {} });
      return null;
    }

    if (
      group.getType() === 'pending' &&
      !router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to the pending group page
      router.replace({ pathname: `/classes/${groupCode}/pending`, query: {} });
      return null;
    }

    if (
      group.getType() !== 'pending' &&
      router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to the usual group page
      router.replace({ pathname: `/classes/${groupCode}`, query: {} });
      return null;
    }

    if (data) {
      const { refetch } = data;
      // $FlowFixMe
      return React.cloneElement(children, { group, refetch });
    } else {
      // $FlowFixMe
      return React.cloneElement(children, { group });
    }
  }
}

const withGroup = graphql(LOAD_BY_GROUP_CODE_QUERY, {
  skip: ({ params }) => params.groupCode === rootGroupCode,
  options: ({ params }) => ({
    variables: { code: `@${params.groupCode}` },
  }),
});

export { GroupContainer };
export default compose(
  withRouter,
  withGroup
)(GroupContainer);
